import mergeWith from 'lodash/mergeWith';
import {
  CreateParams,
  CreateResult,
  DataProvider,
  UpdateParams,
  UpdateResult,
} from 'react-admin';
import uploadDocument from '../uploadDocument';
import config from '../../config';

type FieldMap = {
  update: string[];
  create: string[];
};
const fileFields: Record<string, FieldMap> = {
  cases: {
    update: ['files'],
    create: ['files'],
  },
  'form-answers': {
    update: ['files'],
    create: ['files'],
  },
};

// Single-file fields hold one value rather than a list. The API takes the uploaded file as an
// object, null to clear it, and has no use for the stored key the record already holds.
const singleFileFields: Record<string, FieldMap> = {
  forms: {
    update: ['logo'],
    create: [],
  },
};

type UploadedFile = {
  temporaryKey: string;
  name: string;
};

const concatArrayCustomizer = <T extends unknown>(a: T[], b: T[]) => {
  if (Array.isArray(a) && Array.isArray(b)) return a.concat(b);
  return undefined;
};

type SafeHandler<T extends 'update' | 'create'> = (
  resource: string,
  params: UpdateParams | CreateParams,
) => Promise<T extends 'update' ? UpdateResult : CreateResult>

const prepareSingleFileFields = async (
  fields: string[],
  data: Record<string, any>,
) => {
  const presentFields = fields.filter((field) => data[field] !== undefined);
  if (presentFields.length === 0) return data;

  // A string is the key of the file already stored, sent back when saving without picking a new one
  const keptFields = presentFields.filter((field) => typeof data[field] === 'string');
  const preparedData = Object.fromEntries(
    Object.entries(data).filter(([field]) => !keptFields.includes(field)),
  );

  const filesToUpload = presentFields
    .filter((field) => data[field]?.rawFile instanceof window.File)
    .map((field) => ({
      file: data[field].rawFile as File,
      fileName: field,
    }));
  if (filesToUpload.length === 0) return preparedData;

  const uploadedFiles = await uploadDocument(`${config.apiUrl}/upload`, filesToUpload) as Record<string, UploadedFile[]>;
  filesToUpload.forEach(({ fileName }) => {
    [preparedData[fileName]] = uploadedFiles[fileName];
  });

  return preparedData;
};

const getActionHandler = <T extends 'update' | 'create'>(
  dataProvider: DataProvider,
  action: T,
) => async (
  resource: string,
  originalParams: T extends 'update' ? UpdateParams : CreateParams,
) => {
  const originalActionHandler = dataProvider[action] as SafeHandler<T>;

  const params = Object.keys(singleFileFields).includes(resource)
    ? {
      ...originalParams,
      data: await prepareSingleFileFields(singleFileFields[resource][action], originalParams.data),
    }
    : originalParams;

  if (!Object.keys(fileFields).includes(resource)) return originalActionHandler(resource, params);

  const fields = fileFields[resource][action];

  const fieldsExists = fields.filter((field) => params.data[field] !== undefined);
  if (fieldsExists.length === 0) return originalActionHandler(resource, params);

  const existingFiles = fieldsExists.reduce((data, field) => ({
    ...data,
    [field]: (params.data[field] as any[])
      .filter((fileData) => !(fileData.rawFile instanceof window.File)),
  }), {});

  const filesToUpload = fieldsExists
    .flatMap((field) => (params.data[field] as any[]).map((item) => ({
      file: item.rawFile,
      fileName: field,
    })))
    .filter((fileData) => fileData.file instanceof window.File);

  const uploadedFiles = filesToUpload.length > 0 ? await uploadDocument(`${config.apiUrl}/upload`, filesToUpload) : {};

  const filesData = mergeWith(existingFiles, uploadedFiles, concatArrayCustomizer);
  return originalActionHandler(resource, {
    ...params,
    data: {
      ...params.data,
      ...filesData,
    },
  });
};

const addUploadFeature = (dataProvider: DataProvider) => ({
  ...dataProvider,
  update: getActionHandler(dataProvider, 'update'),
  create: getActionHandler(dataProvider, 'create'),
});

export default addUploadFeature;
